import {
  isSelfPayBenefit,
  requiresAbnSignature,
} from "constants/benefitSelection";
import { orderTypeConstants } from "constants/order";
import { SpecialtyListDictCheckout } from "types/Provider/Provider";

export interface IAbnSelectionProcedure {
  cptCode: string;
  cptDescription: string;
  amount: number;
}

/**
 * Procedures the patient pays for out of pocket, grouped by CPT code.
 *
 * Used for the "not covered" callouts on checkout, the order view and the estimate PDF.
 * Includes both self-pay selections — plain SELF_PAY and SELF_PAY_WITH_ABN (CLE-2659) —
 * since they price identically.
 */
export function getNonCoveredProcedures(order: any): any[] {
  if (!order || order.orderType !== orderTypeConstants.GFE) return [];

  const list =
    order?.serviceDetails?.procedures?.filter((procedure: any) =>
      isSelfPayBenefit(procedure?.benefitsForProvProc?.benefitSelectionType)
    ) || [];

  const groupedByCode: Record<string, any> = list.reduce(
    (acc: Record<string, any>, item: any) => {
      const code = item?.code || "UNKNOWN";
      const amount = item?.amountInCents || 0;
      if (!acc[code]) {
        acc[code] = { ...item, amountInCents: amount };
      } else {
        acc[code].amountInCents = (acc[code].amountInCents || 0) + amount;
      }
      return acc;
    },
    {}
  );

  return Object.values(groupedByCode);
}

/**
 * The CPT codes an ABN must cover, with the patient's estimated cost for each.
 *
 * A procedure row requires an ABN when its benefit selection is REQUIRES_ABN or
 * SELF_PAY_WITH_ABN. The estimated cost is the sum of `patientPortion` for exactly
 * the specialty rows that carry that selection — a code flagged only on the facility
 * contributes only the facility's portion.
 *
 * CLE-2659: this used to require a PRIMARY_PROVIDER row before any ABN would render
 * (commit b8c60089), which meant facility-only orders — imaging, for instance — could
 * never collect one, and anesthesia/pathology amounts were dropped from the total even
 * when flagged. Both restrictions are gone.
 *
 * Returns null when nothing on the order requires an ABN.
 */
export function getAbnSelectionProcedures(
  order: any
): IAbnSelectionProcedure[] | null {
  if (!order || order.orderType !== orderTypeConstants.GFE) return null;

  const allProcedures = order?.serviceDetails?.procedures || [];

  // Which specialties require an ABN, per CPT code.
  const abnSpecialtiesByCode: Record<string, Set<string>> = {};

  allProcedures.forEach((procedure: any) => {
    if (
      !requiresAbnSignature(
        procedure?.benefitsForProvProc?.benefitSelectionType
      ) ||
      !procedure?.specialty
    ) {
      return;
    }
    const { code, specialty } = procedure;
    if (!abnSpecialtiesByCode[code]) {
      abnSpecialtiesByCode[code] = new Set();
    }
    abnSpecialtiesByCode[code].add(specialty);
  });

  if (Object.keys(abnSpecialtiesByCode).length === 0) return null;

  const grouped: Record<string, IAbnSelectionProcedure> = {};

  order.paymentFullBreakdownByCPTCode?.bundledProviderAmounts?.forEach(
    (item: any) => {
      const code = item?.cptCode;
      if (!abnSpecialtiesByCode[code]?.has(item?.specialty)) return;

      if (!grouped[code]) {
        grouped[code] = {
          cptCode: code,
          cptDescription: item?.cptDescription,
          amount: 0,
        };
      }
      grouped[code].amount += item?.patientPortion || 0;
    }
  );

  return Object.values(grouped);
}

export interface IAbnProcedureLine {
  specialty: string;
  providerName: string;
  amount: number;
}

/** One `Self-Pay Rate` payer price, as the API returns them on the order (`abnSelfPayRates`). */
export interface IAbnSelfPayRate {
  cptCode: string;
  providerId: string;
  providerName: string;
  specialty: string;
  priceInCents: number;
}

// Display order of the per-specialty lines under an ABN code; anything else follows.
const ABN_LINE_SPECIALTY_ORDER = [
  "FACILITY",
  "PRIMARY_PROVIDER",
  "ANESTHESIOLOGIST",
];

/**
 * The per-specialty lines shown under one ABN code at checkout — display only.
 *
 * One line per specialty (and provider) the order carries for `cptCode` in its payment
 * breakdown. Anesthesia is left out when the order has more than one CPT code and another
 * code already carries anesthesia: it is billed once for the case, under that other code.
 *
 * Each line is priced at the `Self-Pay Rate` payer's price for the code — what CreateOrder
 * would show with that payer selected (the API runs the same search and returns the rows
 * as `order.abnSelfPayRates`). Every line — facility, physician, anesthesia and any other
 * specialty — must be priced for the very provider the order carries on it; another provider's
 * rate is never used. A line with no self-pay rate for its provider is left out.
 *
 * Which codes are on the ABN, and the amount signed, still come from
 * `getAbnSelectionProcedures`.
 */
export function getAbnProcedureLines(
  order: any,
  cptCode: string
): IAbnProcedureLine[] {
  const allProcedures = order?.serviceDetails?.procedures || [];
  const orderCodes = new Set(
    allProcedures.map((procedure: any) => procedure?.code)
  );
  const anesthesiaUnderOtherCode =
    orderCodes.size > 1 &&
    allProcedures.some(
      (procedure: any) =>
        procedure?.code !== cptCode &&
        procedure?.specialty === "ANESTHESIOLOGIST"
    );

  const selfPayRates: IAbnSelfPayRate[] = (order?.abnSelfPayRates || []).filter(
    (rate: IAbnSelfPayRate) => rate.cptCode === cptCode
  );
  // Every line — facility, physician, anesthesia — is priced only at the self-pay rate of the
  // exact provider the order carries for it.
  const selfPayRateFor = (specialty: string, providerId: string) =>
    selfPayRates.find(
      (rate) => rate.specialty === specialty && rate.providerId === providerId
    );

  const lines: Record<string, IAbnProcedureLine> = {};

  order?.paymentFullBreakdownByCPTCode?.bundledProviderAmounts?.forEach(
    (item: any) => {
      if (item?.cptCode !== cptCode || !item?.specialty) return;
      if (anesthesiaUnderOtherCode && item.specialty === "ANESTHESIOLOGIST") {
        return;
      }

      const key = `${item.specialty}-${item.providerId}`;
      if (lines[key]) return;
      const rate = selfPayRateFor(item.specialty, item.providerId);
      if (!rate) return;
      lines[key] = {
        specialty: item.specialty,
        providerName: rate.providerName,
        amount: rate.priceInCents,
      };
    }
  );

  const rank = (specialty: string) => {
    const index = ABN_LINE_SPECIALTY_ORDER.indexOf(specialty);
    return index === -1 ? ABN_LINE_SPECIALTY_ORDER.length : index;
  };
  return Object.values(lines).sort(
    (a, b) => rank(a.specialty) - rank(b.specialty)
  );
}

/** "Facility – General Hospital": how an ABN line is labelled at checkout and on the ABN PDF. */
export function getAbnLineLabel(line: IAbnProcedureLine): string {
  const specialtyLabel =
    SpecialtyListDictCheckout[
      line.specialty as keyof typeof SpecialtyListDictCheckout
    ] ?? line.specialty;
  return `${specialtyLabel} – ${line.providerName}`;
}

export interface IAbnDocumentRow {
  key: string;
  item: string;
  /** Null for a code heading whose lines carry the prices. */
  amount: number | null;
  isLine: boolean;
}

/**
 * The rows the ABN PDF prints from a signed snapshot, mirroring the checkout list: a
 * `code – description` heading followed by one priced row per line. A snapshot signed before
 * lines were saved prints the code with its single amount, as it always did.
 */
export function getAbnDocumentRows(
  procedures: (IAbnSelectionProcedure & { lines?: IAbnProcedureLine[] })[]
): IAbnDocumentRow[] {
  return (procedures || []).flatMap((procedure) => {
    const item = `${procedure.cptCode} – ${procedure.cptDescription}`;
    if (!procedure.lines?.length) {
      return [
        {
          key: procedure.cptCode,
          item,
          amount: procedure.amount,
          isLine: false,
        },
      ];
    }
    return [
      { key: procedure.cptCode, item, amount: null, isLine: false },
      ...procedure.lines.map((line) => ({
        key: `${procedure.cptCode}-${line.specialty}-${line.providerName}`,
        item: getAbnLineLabel(line),
        amount: line.amount,
        isLine: true,
      })),
    ];
  });
}
